import Visibility from "@mui/icons-material/Visibility";
import Import from "assets/img/svg/navbar/import";
import Upload from "assets/img/svg/upload";
import Beneficiary from "components/Beneficiar";
import BeneficiaryDocumentsTable from "components/BeneficiaryDocumentsTable";
import ViewToggle from "components/BeneficiaryDocumentsTable/ViewToggle";
import BeneficiaryFormContainer from "components/BeneficiaryFormContainer";
import ImportData from "components/ImportData";
import { getPendingImports } from "components/ImportData/utils";
import ModalBox from "components/Modal";
import { openEditMode } from "components/NewForm/utils";
import AuthPage from "components/shared/AuthPage";
import BeneficiarListItem from "components/shared/BeneficiarListItem";
import type { Action } from "components/shared/ButtonMenu";
import ElementList from "components/shared/ElementList";
import { EL_SORT_STORAGE_KEY, getPersistedFilterOrder } from "components/shared/ElementList/sortStorage";
import DetailPanelScroll from "components/shared/DetailPanelScroll";
import { LIST_SKELETON_COUNT } from "components/shared/TextSkeleton";
import BulkUpdatesContainer from "containers/BulkUpdatesContainer";
import { PortalType } from "contexts/portal.context";
import { useCompanySettings } from "hooks/useCompanySettings";
import useEditModeLock from "hooks/useEditModeLock";
import useListItemPreview from "hooks/useListItemPreview";
import useSelectFirstRouteItem from "hooks/useSelectFirstRouteItem";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
    exportBeneficiaryList,
    fetchBeneficiaries,
    resetBeneficiaries,
    resetBeneficiariesList,
    setBeneficiarPreview,
} from "store/beneficiaryStore";
import { getBeneficiaryTags } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { deleteTerm } from "store/termStore";
import type { TParamsPagination } from "store/types";
import { EPortalType } from "store/types";

import InviteAuthorModal from "./InviteAuthorModal";

/** Must match each `orderOptions[].name` below (used for persisted-sort validation on first fetch). */
const BENEFICIARY_SORT_FIELD_NAMES = ["creationDate", "lastModifiedDate", "surname"] as const;

const Beneficiaries = () => {
    const portalType = useContext(PortalType);
    const params = useParams();
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { beneficiars, beneficiarLoadStatus, countBeneficiars, beneficiar, isListRefreshing, successionStatus } =
        useAppSelector((state) => state.beneficiary);
    const isListReady = beneficiarLoadStatus !== "idle" && !isListRefreshing;
    const isInitialLoad = beneficiarLoadStatus === "idle";
    const tagBeneficiarList = useAppSelector((state) => state.beneficiary.tags);
    const { customFields } = useAppSelector((state) => state.customFields);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);

    const { companyInfos, getCompanySettings } = useCompanySettings();
    const usesPortal = getCompanySettings("uses_portal");
    const routeBeneficiaryId = params.id ? Number(params.id) : null;
    const usesDocumentsTracking = !!getCompanySettings("uses_beneficiary_documents_tracking");
    // List (default) or beneficiaries × documents table, persisted per browser
    const [view, setView] = useState<"list" | "table">(
        localStorage.getItem("beneficiaries_view") === "table" ? "table" : "list",
    );
    const switchView = (next: "list" | "table") => {
        localStorage.setItem("beneficiaries_view", next);
        setView(next);
    };
    const isDocumentsTable = usesDocumentsTracking && view === "table";

    const [currentBeneficiar, setCurrentBeneficiar] = useState<number>(routeBeneficiaryId ?? -1);
    const [modalType, setModalType] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [showId, setShowId] = useState(localStorage.getItem("show_id_beneficiaries") === "true");
    // Deceased beneficiaries stay listed unless the user explicitly hides them.
    const [showDeceased, setShowDeceased] = useState(localStorage.getItem("show_deceased_beneficiaries") !== "false");
    // Bumped to replay the list search after an action that changed it outside of the list (succession).
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const deceasedParams: Partial<TParamsPagination> = showDeceased ? { includeDeceased: "1" } : {};

    /**
     * Object that contains refs for each item within list.
     */
    const elementListRef = {};

    const orderOptions: { name: string; label: string }[] = [
        { name: "creationDate", label: t("Filters.CreationDate") },
        { name: "lastModifiedDate", label: t("Filters.LastModifiedDate") },
        { name: "surname", label: t("Filters.BeneficiaryName") },
    ];

    const isSelectingFirst = useSelectFirstRouteItem(
        beneficiars,
        // The documents table has no selected row. Passing a number keeps the list URL untouched.
        isDocumentsTable ? routeBeneficiaryId ?? 0 : routeBeneficiaryId,
        "/beneficiars",
        setCurrentBeneficiar,
    );

    const { selectItem: setBeneficiar } = useListItemPreview({
        items: beneficiars,
        routeId: routeBeneficiaryId,
        currentId: beneficiar?.id,
        pathPrefix: "/beneficiars",
        enabled: isListReady,
        onPreview: (selectedBeneficiar) => {
            setCurrentBeneficiar(selectedBeneficiar.id);
            dispatch(setBeneficiarPreview(selectedBeneficiar));
        },
    });

    useEffect(() => {
        // when create new beneficiar by invitation modal, set currentBeneficiar when state is changing
        if (!beneficiar?.id) return;
        setCurrentBeneficiar(beneficiar.id);
    }, [beneficiar]);

    useEffect(() => {
        const sortFilters = getPersistedFilterOrder(EL_SORT_STORAGE_KEY.beneficiaries, BENEFICIARY_SORT_FIELD_NAMES);
        dispatch(fetchBeneficiaries({ limit: 100, ...sortFilters, ...deceasedParams }));
        return () => {
            dispatch(resetBeneficiariesList());
            dispatch(resetBeneficiaries());
        };
    }, [dispatch]);

    // A succession marks the deceased and creates their heirs: replay the search so the list shows both.
    const previousSuccessionStatus = useRef(successionStatus);
    useEffect(() => {
        const justSucceeded = previousSuccessionStatus.current === "pending" && successionStatus === "succeeded";
        previousSuccessionStatus.current = successionStatus;
        if (justSucceeded) setListRefreshKey((key) => key + 1);
    }, [successionStatus]);

    const deleteTermFn = useCallback(
        (id: number) => {
            dispatch(deleteTerm(id));
        },
        [dispatch],
    );

    const handleExport = () => dispatch(exportBeneficiaryList());

    const handleIdDisplay = () => {
        localStorage.setItem("show_id_beneficiaries", `${!showId}`);
        setShowId(!showId);
    };

    const handleDeceasedDisplay = () => {
        const next = !showDeceased;
        localStorage.setItem("show_deceased_beneficiaries", `${next}`);
        setShowDeceased(next);
        const sortFilters = getPersistedFilterOrder(EL_SORT_STORAGE_KEY.beneficiaries, BENEFICIARY_SORT_FIELD_NAMES);
        dispatch(fetchBeneficiaries({ limit: 100, ...sortFilters, ...(next ? { includeDeceased: "1" } : {}) }));
    };

    const popoverMenu = {
        Imports: [
            {
                label: t("ImportData.ImportBeneficiar"),
                icon: <Upload />,
                action: () => setModalType("importBenef"),
            },
            {
                label: t("ImportData.ImportTerms"),
                icon: <Upload />,
                action: () => setModalType("importTerms"),
            },
            {
                label: t("BulkUpdates.BulkUpdate"),
                icon: <Upload />,
                action: () => setModalType("updates"),
            },
        ],
        Exports: [
            {
                label: t("ImportData.ExportBeneficiar"),
                icon: <Import width="16" height="16" />,
                action: () => handleExport(),
            },
        ],
        Affichage: [
            {
                label: `${showId ? t("Beneficiars.Hide") : t("Beneficiars.Show")} ${t(
                    "Beneficiars.ShowBeneficiaryId",
                )}`,
                icon: <Visibility sx={{ width: "16px", height: "16px" }} />,
                action: () => handleIdDisplay(),
            },
            {
                label: showDeceased ? t("Heirs.HideDeceased") : t("Heirs.ShowDeceased"),
                icon: <Visibility sx={{ width: "16px", height: "16px" }} />,
                action: () => handleDeceasedDisplay(),
            },
        ],
    };

    useEditModeLock(editMode);

    const popoverPress = () => {
        const imports = popoverMenu.Imports.filter((item) => item.label === t("ImportData.ImportBeneficiar"));
        return { ...popoverMenu, Imports: imports };
    };

    useEffect(() => {
        if (tagBeneficiarList === null) dispatch(getBeneficiaryTags());
    }, [tagBeneficiarList, dispatch]);

    const handleNewBeneficiar = (action?: "invite-author" | "create-beneficiar") => {
        if (action === "invite-author") {
            setModalType("inviteAuthor");
        } else {
            navigate("/beneficiaries/new");
        }
    };

    const newBeneficiarMenuActions: Action[] = [
        {
            name: t("Beneficiars.CreateNewBeneficiar"),
            action: () => handleNewBeneficiar("create-beneficiar"),
            rightName: "",
        },
        {
            name: t("Beneficiars.InviteNewAuthor"),
            action: () => handleNewBeneficiar("invite-author"),
            rightName: t("Beneficiars.SimplifiedCreation"),
            subtitle: t("Beneficiars.InviteNewAuthorToComplete"),
        },
    ];

    const pendingJobs = getPendingImports({ jobs, fakeImports: pendingImports, type: ["beneficiar"] });

    const listMenu = portalType === EPortalType.BOOK ? popoverMenu : popoverPress();
    // The display options only concern the list
    const tableMenu = Object.fromEntries(Object.entries(listMenu).filter(([group]) => group !== "Affichage"));

    return (
        <AuthPage userAuthenticated={true} pageName="beneficiaries">
            {isDocumentsTable ? (
                <BeneficiaryDocumentsTable
                    onSwitchToList={() => switchView("list")}
                    onOpenBeneficiary={(id) => {
                        switchView("list");
                        navigate(`/beneficiars/${id}`);
                    }}
                    popoverMenu={tableMenu}
                    pendingJobs={pendingJobs}
                />
            ) : (
                <>
                    <ElementList
                        elements={beneficiars}
                        currentElement={currentBeneficiar}
                        setCurrentElement={setBeneficiar}
                        elementsStatus={beneficiarLoadStatus}
                        elementCount={countBeneficiars}
                        elementData={{
                            label: t("Beneficiars.Beneficiars"),
                            actionLabel: t("Beneficiars.NewBeneficiary"),
                            action: handleNewBeneficiar,
                        }}
                        elementSearchFunc={(options: TParamsPagination) =>
                            dispatch(fetchBeneficiaries({ ...options, ...deceasedParams }))
                        }
                        popoverMenu={listMenu}
                        editMode={editMode}
                        tagList={tagBeneficiarList}
                        orderOptions={orderOptions}
                        showTags
                        hasMultipleActionBtn={usesPortal && portalType === EPortalType.BOOK}
                        btnMenuActions={newBeneficiarMenuActions}
                        isInitialLoad={isInitialLoad}
                        showListLoading={!isListReady}
                        listSkeleton={Array.from({ length: LIST_SKELETON_COUNT }, (_, index) => (
                            <BeneficiarListItem key={`beneficiary-list-skeleton-${index}`} index={index} isLoading />
                        ))}
                        pendingJobs={pendingJobs}
                        sortStorageKey={EL_SORT_STORAGE_KEY.beneficiaries}
                        refreshKey={listRefreshKey}
                        headerActions={
                            usesDocumentsTracking ? (
                                <ViewToggle active={false} onClick={() => switchView("table")} />
                            ) : undefined
                        }
                    >
                        <>
                            {[...beneficiars].map((element, index) => (
                                <BeneficiarListItem
                                    element={element}
                                    currentElementId={currentBeneficiar}
                                    index={index}
                                    action={setBeneficiar}
                                    key={`beneficiary-list-item-${index}`}
                                    disabled={editMode && element.id != currentBeneficiar}
                                    showId={showId}
                                    ref={(el: HTMLDivElement | null) => (elementListRef[element.id] = el)}
                                />
                            ))}
                        </>
                    </ElementList>
                    <DetailPanelScroll>
                        <Beneficiary
                            deleteTerm={deleteTermFn}
                            openEditBeneficiar={() =>
                                openEditMode({
                                    elementListRef: { ...elementListRef },
                                    elementId: currentBeneficiar.toString(),
                                    setEditMode,
                                })
                            }
                            editMode={editMode}
                            noBeneficiaries={isListReady && beneficiars.length === 0}
                            isListLoading={!isListReady || isSelectingFirst}
                        />
                        {editMode && (
                            <BeneficiaryFormContainer
                                mode="edit-page"
                                editModeActive={editMode}
                                closeEditMode={() => setEditMode(false)}
                            />
                        )}
                    </DetailPanelScroll>
                </>
            )}
            {portalType === EPortalType.BOOK && usesPortal && (
                <InviteAuthorModal
                    open={modalType === "inviteAuthor"}
                    onClose={() => setModalType("")}
                    companyCurrency={companyInfos?.currency}
                />
            )}
            <ModalBox
                open={modalType === "importBenef"}
                dictionaryKey={"beneficiars"}
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportBeneficiar")}
            >
                <ImportData dictionaryKey={"beneficiars"} close={() => setModalType("")} />
            </ModalBox>
            {portalType === EPortalType.BOOK && (
                <ModalBox
                    open={modalType === "importTerms"}
                    dictionaryKey={"terms"}
                    onClose={() => setModalType("")}
                    mainTitle={t("ImportData.ImportTerms")}
                >
                    <ImportData dictionaryKey={"terms"} close={() => setModalType("")} />
                </ModalBox>
            )}
            {modalType === "updates" && (
                <BulkUpdatesContainer
                    openModal={modalType === "updates"}
                    closeModal={() => setModalType("")}
                    from="beneficiars"
                />
            )}
        </AuthPage>
    );
};

export default Beneficiaries;
